import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import i18n from 'i18next'
import * as countries from 'i18n-iso-countries'
import { useTheme, useMediaQuery, Box } from '@mui/material'

import { SideContent } from 'components-shared'
import CardPreview, {
  CardBlockInfo,
  CardHeader,
  CardBody,
  CardHeaderTitle,
  CardHeaderEmailLink
} from 'packages/ui/CardPreview'
import SlideInModal from 'components/Modal/SlideInModal'
import Loading from 'components/global/Loading'
import { gql } from '@apollo/client'
import { Tooltip } from '@mui/material'
import { getCivilityLabel, getVisitReasonLabel } from '../helpers'

const USER_OPERATION = gql`
  query PortalUser($id: ID!) {
    user: portalUser(id: $id) {
      id
      email
      country
      civility
      visit_reason
      site {
        id 
        name
      }
      account_parent {
        sf_id
        name
      }
      first_name
      last_name
      created_at
      newsletter
      device
      os_name
      os_version
      password_request_confirmation_code
      password_request_requested_at
      phone_number
      browser_name
    }
  }
`

const Card: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))

  const { loading, error, data } = useQuery(USER_OPERATION, {
    variables: { id }
  })

  const handleClose = () => {
    navigate('..')
  }

  const cardContent = (
    <>
      {data && (
        <>
          <CardHeader>
            <CardHeaderTitle>{data.user.first_name} {data.user.last_name}</CardHeaderTitle>
            <CardHeaderEmailLink
              onClick={() => {
                navigator.clipboard.writeText(data.user.email)
              }}
            >
              <Tooltip title={t('statistics.customers.copy_email')} placement={'left'} arrow>
                <Box
                  sx={{
                    color: '#7A8096',
                    fontSize: '12px',
                    fontWeight: 400,
                    fontFamily: 'Poppins',
                  }}
                >
                  <i className={'fa-light fa-copy'} style={{fontSize: '14px', marginRight: 4}} />
                  {data.user.email}
                </Box>
              </Tooltip>
            </CardHeaderEmailLink>
          </CardHeader>
          <CardBody grid>
            {data.user.civility && (
              <CardBlockInfo
                title={t('statistics.customers.table.civility')}
                subtitle={getCivilityLabel(data.user.civility, t)}
              />
            )}
            {data.user.visit_reason && (
              <CardBlockInfo
                title={t('statistics.customers.table.visit_reason')}
                subtitle={getVisitReasonLabel(data.user.visit_reason, t)}
              />
            )}
            <CardBlockInfo
              title={t('statistics.customers.table.account')}
              subtitle={data.user.account_parent.name}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.site')}
              subtitle={data.user.site.name}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.phone_number')}
              subtitle={data.user.phone_number || '-'}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.country')}
              subtitle={
                data.user.country 
                  ? countries.getName(data.user.country.toLowerCase(), i18n.language) || data.user.country
                  : '-'
              }
            />
            <CardBlockInfo
              title={t('statistics.customers.table.created_at')}
              subtitle={new Date(data.user.created_at).toLocaleDateString()}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.os_name')}
              subtitle={data.user.os_name || '-'}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.browser_name')}
              subtitle={data.user.browser_name || '-'}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.password_request_confirmation_code')}
              subtitle={data.user.password_request_confirmation_code || '-'}
            />
            <CardBlockInfo
              title={t('statistics.customers.table.password_request_requested_at')}
              subtitle={
                data.user.password_request_requested_at
                  ? new Date(data.user.password_request_requested_at).toLocaleDateString()
                  : '-'
              }
              tooltiptitle={
                data.user.password_request_requested_at
                  ? new Date(data.user.password_request_requested_at).toLocaleTimeString()
                  : null
              }
            />
          </CardBody>
        </>
      )}
    </>
  )

  if (isLargeScreen) {
    return (
      <SideContent style={{ width: 420 }}>
        <CardPreview
          title={t('statistics.customers.card.title')}
          loading={loading}
          error={error ? t('shared.error') : null}
          onClose={handleClose}>
          {cardContent}
        </CardPreview>
      </SideContent>
    )
  }

  return (
    <SlideInModal
      title={t('statistics.customers.card.title')}
      onClose={handleClose}
      width={isSmallScreen ? '100%' : '450px'}
      fullScreen={isSmallScreen}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Loading />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {cardContent}
        </Box>
      )}
    </SlideInModal>
  )
}

export default Card
